// src/scanAdmin/index.js - the manager app's Scan card (v133.118), on the Top 50 page ("📷 Scan").
//
// Everyone who can open the Top 50 page gets the "Open Scan" button. Head Office also gets the data
// upload (the DSR SKU sales report, the Cubes product details and price matrix files: any of the three,
// one per country each, in any order) and the switch that shows Scan in the staff
// app. The file is read HERE, in the browser (parse.js), and only its summaries are sent, in chunks;
// the Worker writes only the fields it allows (src/worker/scan.js cleanUploadRows) and checks Head
// Office on every request.
//
// Auth = the admin Bearer (DB.getToken), absolute Worker URL (DB.workerUrl).

import { parseDsr, chunk, pickSheet, parseCatalogue, parseMatrix, parseTeal, parseStock, parsePromo } from './parse.js';
import { ensureXlsx } from '../shared/ensure-xlsx.js';   // v136.8: the spreadsheet library loads only when it is used

const CHUNK = 1500;
let _status = null;
let _busy = false;

function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
async function api(path, body) {
  const token = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  const base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = 'Bearer ' + token;
  const res = await fetch(base + path, { cache: 'no-store', method: 'POST', headers, body: JSON.stringify(body || {}) });
  const d = await res.json().catch(() => ({}));
  if (!res.ok || d.ok === false) throw new Error(d.error || ('HTTP ' + res.status));
  return d;
}
function root() { return document.getElementById('scanAdminRoot'); }
function isHeadOffice() {
  return typeof currentAdmin !== 'undefined' && currentAdmin && String(currentAdmin.role || '').toLowerCase() === 'admin';
}
function fmtDay(iso) {
  if (!iso) return '';
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00Z');
  return isNaN(d) ? String(iso) : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}
function line(label, u) {
  if (!u) return '<div style="font-size:13px;color:var(--sub)">' + esc(label) + ': no sales yet</div>';
  return '<div style="font-size:13px">' + esc(label) + ': sales ' + esc(fmtDay(u.period_start)) + ' to ' + esc(fmtDay(u.period_end)) + (u.totals_only ? ' (weekly totals)' : '') +
    ' · ' + esc(u.skus) + ' products · uploaded ' + esc(fmtDay(u.uploaded_at)) + '</div>';
}
// v136.20: "20 Oct 2026, 10:55" in Dubai (UTC+4), for a file that is as at its upload
export function fmtDayTime(iso, noYear) {
  const t = Date.parse(iso || '');
  if (!isFinite(t)) return '';
  const d = new Date(t + 4 * 3600000);
  const day = d.toLocaleDateString('en-GB', Object.assign({ day: 'numeric', month: 'short', timeZone: 'UTC' }, noYear ? {} : { year: 'numeric' }));
  return day + ', ' + d.toISOString().slice(11, 16);
}
// A stock or promotions record's "as at": the day in its file name, or (v136.20, no date in the name) the
// upload's own date and time.
export function asAt(rec, noYear) {
  if (!rec) return '';
  return rec.as_of_upload && rec.at ? fmtDayTime(rec.at, noYear) : fmtDay(rec.as_of);
}
// v136.16: the stock files loaded for a country (v136.20: each part its own text, so the Arabic finds it)
function stockLine(label, c) {
  const s = c && c.stock, w = c && c.warehouse, p = c && c.promo;
  return '<div style="font-size:13px"><span>' + esc(label) + ' stock: ' +
    (s ? 'stores as at ' + esc(asAt(s)) + ' (' + esc(s.stores) + ' stores' + (s.negative ? ', ' + esc(s.negative) + ' negative lines' : '') + ')' : 'no store stock yet') + '</span>' +
    '<span>' + (w ? ' · warehouse as at ' + esc(asAt(w)) : ' · no warehouse stock yet') + '</span>' +
    // v136.20: the active promotions
    '<span>' + (p ? ' · promotions as at ' + esc(asAt(p)) + ' (' + esc(p.promotions) + ' promotions, ' + esc(p.stores) + ' stores)' : ' · no promotions yet') + '</span></div>';
}
// v136.16: what the database still needs, in words
function dbMessage(msg) {
  if (/totals_only/i.test(msg)) return 'Weekly sales files need the v240-0 database update first.';
  if (/scan_promos|v241-0/i.test(msg)) return 'Scan promotions need the v241-0 database update first.';   // v136.20
  return /scan_|relation|does not exist/i.test(msg) ? 'Scan needs the v238-0 database update first.' : msg;
}

export function openScan() {
  try { window.open('/scan?from=admin', '_blank', 'noopener'); } catch (e) { location.href = '/scan?from=admin'; }
}

export async function render() {
  const el = root();
  if (!el) return;
  const ho = isHeadOffice();
  let html = '<div class="card"><div class="card-title">📷 Scan</div>' +
    '<div style="font-size:13px;color:var(--sub);line-height:1.6;margin-bottom:12px">Take a photo of a product in the bay and see its sales, stock and what to do with it, for any store. ' +
    'It opens on its own page: <strong>shift.elctoys.com/scan</strong>.</div>' +
    '<button class="btn btn-green" onclick="ScanAdmin.openScan()">📷 Open Scan</button>' +
    '<div id="scanAdminStatus" style="margin-top:14px;display:flex;flex-direction:column;gap:4px"><div style="font-size:13px;color:var(--sub)">Loading…</div></div></div>';
  if (ho) {
    html += '<div class="card"><div class="card-title">Scan data (Head Office)</div>' +
      // v136.20: the daily files from ALGT, named and in the order to upload them
      '<div style="font-size:13px;color:var(--sub);line-height:1.6;margin-bottom:12px">Upload the daily .xlsx files for each country (UAE or Qatar in the file name). Each is recognised by its columns. Upload them in this order:' +
      '<ol style="margin:6px 0 6px 18px;padding:0"><li><strong>Master data</strong>: every product (its Products sheet) and every barcode (its Barcodes sheet). It never clears a price or cost loaded before.</li>' +
      '<li><strong>Sales</strong>: the sales by day. A new file replaces the days it covers.</li>' +
      '<li><strong>Stock</strong>: each store\'s stock, its own price and what it has on order, with the order (LPO) date; the HEAD OFFICE lines are the warehouse. As at the upload time.</li>' +
      '<li><strong>Promotions</strong>: the promotions active now. Each file replaces the country\'s list.</li></ol>' +
      '<span>The older files still load: the DSR SKU sales report, the TEAL Report by branch, Product details main barcode, the Price Matrix, and the Cubes and Falcon Current SOH files (their date is read from the file name, e.g. "_20261019"). Scan shows the last 28 days of sales held for each country.</span> ' +
      '<span>Files are read in this browser. From the sales report only daily totals per product per store are sent: never the bill numbers, the bill remarks (customer and order numbers) or the discount reasons. Cost and margin are shown to Head Office only.</span></div>' +
      '<input type="file" id="scanAdminFile" accept=".xlsx,.xls" multiple onchange="ScanAdmin.handleFiles(this.files)" style="font-size:13px">' +
      '<div id="scanAdminProgress" style="margin-top:12px;font-size:13px;line-height:1.6"></div>' +
      // v136.19: the switch for store devices lives in App Status, with the other "on for the stores" switches
      '<div style="font-size:12px;color:var(--sub);margin-top:16px">Scan in the staff app (store devices) is switched on or off in App Status (the status pill at the top). Managers can always open it from here.</div></div>';
  }
  el.innerHTML = html;
  await refreshStatus();
}

async function refreshStatus() {
  const box = document.getElementById('scanAdminStatus');
  try {
    _status = await api('/api/scan/status', {});
    const cat = (label, c) => {
      const d = c && c.details, m = c && c.matrix;
      return '<div style="font-size:13px">' + esc(label) + ' catalogue: ' +
        (d ? esc(d.products.toLocaleString()) + ' products, ' + esc(d.barcodes.toLocaleString()) + ' main barcodes (' + esc(fmtDay(d.at)) + ')' : 'no product details yet') +
        (m ? ' · ' + esc(m.barcodes.toLocaleString()) + ' linked barcodes, prices from ' + esc(m.branch || 'one branch') + ' (' + esc(fmtDay(m.at)) + ')' : ' · no price matrix yet') + '</div>';
    };
    if (box) box.innerHTML = line('UAE', _status.data && _status.data.UAE) + cat('UAE', _status.catalogue && _status.catalogue.UAE) + stockLine('UAE', _status.catalogue && _status.catalogue.UAE) +
      line('Qatar', _status.data && _status.data.QATAR) + cat('Qatar', _status.catalogue && _status.catalogue.QATAR) + stockLine('Qatar', _status.catalogue && _status.catalogue.QATAR) +
      '<div style="font-size:12px;color:var(--sub)">In the staff app: ' + (_status.enabled ? 'switched on' : 'switched off') + '</div>';
  } catch (e) {
    if (box) box.innerHTML = '<div style="font-size:13px;color:var(--red)">' + esc(dbMessage(e.message)) + '</div>';
  }
}

export async function setEnabled(on) {
  try {
    await api('/api/scan/config', { enabled: !!on });
    if (typeof showToast === 'function') showToast(on ? 'Scan is on in the staff app' : 'Scan is off in the staff app');
  } catch (e) {
    if (typeof showToast === 'function') showToast('Could not change it: ' + e.message);
  }
  if (root()) refreshStatus();
}

// ── the App Status switch (v136.19) ───────────────────────────────────────────────────────────────
// Matt, 20 Oct 2026: "is it not a weird place to save it in the top 50?". The switch that shows Scan on store
// devices moved from the Top 50 page to App Status, next to Arabic, Dark mode and Leave requests, and looks
// like them. Same setting as before (app_config scan_config, through /api/scan/config, Head Office only on the
// Worker), so what was ticked stays ticked. null = not read yet or the read failed: no button is offered then,
// so nobody saves from a made-up state.
let _switchOn = null;
export async function loadSwitch() {
  renderSwitch('Loading...');
  try {
    const d = await api('/api/scan/status', {});
    _switchOn = !!d.enabled;
    renderSwitch();
  } catch (e) {
    _switchOn = null;
    renderSwitch('Could not load this setting, so it cannot be changed right now. Close and reopen to try again.');
  }
}
export function renderSwitch(msg) {
  const box = document.getElementById('scanSwitchBox');
  if (!box) return;
  if (msg || _switchOn === null) { box.textContent = msg || 'Loading...'; return; }
  const on = _switchOn;
  box.innerHTML =
    '<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap">' +
      '<span style="display:inline-block;border-radius:999px;padding:2px 10px;font-weight:700;' + (on ? 'background:var(--ok-bg);color:var(--ok-deep)' : 'background:var(--surface-3);color:var(--text)') + '">' +
        (on ? 'ON: the 📷 Scan tab is on store devices' : 'OFF: managers only') + '</span>' +
      (isHeadOffice()
        ? '<button type="button" class="btn btn-outline btn-sm" id="scanSwitchBtn" onclick="ScanAdmin.setSwitch(' + (on ? 'false' : 'true') + ')">' + (on ? 'Turn Scan off for the stores' : 'Turn Scan on for the stores') + '</button>'
        : '<span>Head Office can change this.</span>') +
    '</div>' +
    '<div style="font-size:11px;margin-top:6px;line-height:1.45">Head Office and area managers can always open Scan (Trade, Scan, or shift.elctoys.com/scan). Its data is uploaded in Trade, Scan.</div>';
}
export async function setSwitch(on) {
  const btn = document.getElementById('scanSwitchBtn');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving...'; }
  try {
    await api('/api/scan/config', { enabled: !!on });
    _switchOn = !!on;
    if (typeof showToast === 'function') showToast(on ? 'Scan is on for the stores.' : 'Scan is off for the stores.', 'success');
  } catch (e) {
    if (typeof showToast === 'function') showToast('Could not change the Scan setting: ' + (e && e.message ? e.message : 'unknown'), 'error');
  }
  renderSwitch();
}

function readFile(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('Could not read ' + file.name));
    r.readAsArrayBuffer(file);
  });
}

// The sheet to read: the DSR's "data" sheet, else the first sheet that is one of the files; v136.20: or
// the new master data file: a Products sheet and a Barcodes sheet read together (parse.js pickSheet).
function sheetRows(buf) {
  if (typeof XLSX === 'undefined') throw new Error('The spreadsheet reader has not loaded. Reload the page and try again.');
  const wb = XLSX.read(buf, { type: 'array' });
  const seen = {};
  const rowsOf = (n) => (seen[n] || (seen[n] = XLSX.utils.sheet_to_json(wb.Sheets[n], { header: 1, raw: true, defval: null })));
  const hit = pickSheet(wb.SheetNames, rowsOf);
  if (hit) return hit;
  throw new Error('This is not one of the Scan files (no "Prod Code"/"Branch", "Product Code"/"Product Name", "Product Code"/"Barcode", "Branch Name"/"On-Hand Qty Total" or "Branch"/"Discounted Selling Price" heading found)');
}

// replace: the first chunk clears what the country had before (the store stock file is the whole picture)
async function sendCatalogue(country, kind, rows, label, step, replace) {
  const jobs = chunk(rows, CHUNK);
  if (!jobs.length && replace) jobs.push([]);
  let without = false;   // v136.20: the Worker left out the v241-0 fields (each store's price, on order)
  for (let i = 0; i < jobs.length; i++) {
    step(label + ': sending ' + kind.replace('_', ' ') + ' ' + (i + 1) + ' of ' + jobs.length + '…');
    const d = await api('/api/scan/upload/catalogue', { country, kind, rows: jobs[i], replace: !!replace && i === 0 });
    if (d && d.without) without = true;
  }
  return { without };
}

// A weekly file whose name carries no dates: ask which week it covers.
function askPeriod(name) {
  const a = (typeof prompt === 'function') ? prompt('Which days does "' + name + '" cover? Type the first and last day, e.g. 2026-10-12 2026-10-18') : '';
  const m = String(a || '').match(/(\d{4}-\d{2}-\d{2})\D+(\d{4}-\d{2}-\d{2})/);
  if (!m || m[1] > m[2]) throw new Error(name + ': no dates given, so it was not loaded.');
  return { start: m[1], end: m[2] };
}

function todayDubai() {
  return new Date(Date.now() + 4 * 3600000).toISOString().slice(0, 10);   // Dubai is UTC+4
}

export async function handleFiles(files) {
  if (_busy || !files || !files.length) return;
  _busy = true;
  const out = document.getElementById('scanAdminProgress');
  const say = (h) => { if (out) out.innerHTML = h; };
  const done = [];
  try {
    for (const file of Array.from(files)) {
      say(done.join('') + '⏳ Reading ' + esc(file.name) + '…');
      await ensureXlsx();   // v136.8: loads on first use; a failed load is reported like any other failure below
      const sheet = sheetRows(await readFile(file));
      if (sheet.kind === 'details' || sheet.kind === 'matrix') {
        const cat = sheet.kind === 'details' ? parseCatalogue(sheet.aoa, file.name, sheet.barcodes) : parseMatrix(sheet.aoa, file.name);
        if (!cat.country) throw new Error(file.name + ': could not tell whether this is the UAE or the Qatar file. Put UAE or Qatar in the file name.');
        const label = (cat.country === 'QATAR' ? 'Qatar' : 'UAE') + (sheet.kind === 'details' ? ' product details' : ' price matrix');
        const step = (t) => say(done.join('') + '⏳ ' + esc(t));
        if (sheet.kind === 'details') await sendCatalogue(cat.country, 'catalogue', cat.products, label, step);
        else await sendCatalogue(cat.country, 'prices', cat.prices, label, step);
        await sendCatalogue(cat.country, 'barcodes', cat.barcodes, label, step);
        await api('/api/scan/upload/catalogue-done', { country: cat.country, file: sheet.kind, filename: file.name,
          products: sheet.kind === 'details' ? cat.products.length : cat.prices.length, barcodes: cat.barcodes.length, branch: (cat.branches || [])[0] || '' });
        done.push('<div>✅ ' + esc(label) + ': ' + (sheet.kind === 'details' ? cat.products.length.toLocaleString() + ' products, ' : cat.prices.length.toLocaleString() + ' prices, ') +
          cat.barcodes.length.toLocaleString() + ' barcodes' + (cat.skipped ? ' (' + cat.skipped + ' empty, dummy or repeated barcodes skipped)' : '') + '.</div>');
        say(done.join(''));
        continue;
      }
      if (sheet.kind === 'stock') {
        const st = parseStock(sheet.aoa, file.name);
        if (!st.country) throw new Error(file.name + ': could not tell whether this is the UAE or the Qatar file. Put UAE or Qatar in the file name.');
        const label = (st.country === 'QATAR' ? 'Qatar' : 'UAE') + (st.stores.length ? ' store stock' : ' warehouse stock');
        // v136.20 (Matt, 20 Oct 2026: "i want the date to be linked with the upload time"): a date in the file
        // name (the Cubes / Falcon exports) is kept; without one (the new daily files) the stock is as at the upload,
        // and the Worker records the time (catalogue-done as_of is sent only when the name gave it)
        const asOf = st.asOf || todayDubai();
        const named = st.asOf ? { as_of: st.asOf } : {};
        const step = (t) => say(done.join('') + '⏳ ' + esc(t));
        const bits = [];
        let without = false;
        if (st.stores.length) {
          without = (await sendCatalogue(st.country, 'store_stock', st.stock.map((r) => Object.assign({ as_of: asOf }, r)), label, step, true)).without;
          // the price most stores charge goes on the product, so a store with no line charges that
          if (st.prices.length && !without) await sendCatalogue(st.country, 'prices', st.prices, label, step);
          await api('/api/scan/upload/catalogue-done', Object.assign({ country: st.country, file: 'stock', filename: file.name, products: st.stock.length, stores: st.stores.length, negative: st.negative, branches: st.stores,
            store_price: st.hasPrice && !without, on_order: st.hasOrder && !without, on_order_lines: without ? 0 : st.onOrderLines }, named));
          bits.push(st.stores.length + ' stores, ' + st.stock.length.toLocaleString() + ' store lines' + (st.hasOrder && !without ? ', ' + st.onOrderLines.toLocaleString() + ' with stock on order' : ''));
        }
        if (st.warehouse.length) {
          await sendCatalogue(st.country, 'warehouse', st.warehouse, label, step);
          await api('/api/scan/upload/catalogue-done', Object.assign({ country: st.country, file: 'warehouse', filename: file.name, products: st.warehouse.length, branch: st.warehouses[0] || '' }, named));
          bits.push('warehouse (' + st.warehouses.join(', ') + '), ' + st.warehouse.length.toLocaleString() + ' products');
        }
        // each part its own text, so the Arabic finds it
        done.push('<div>✅ <span>' + esc(label) + ' as at ' + esc(st.asOf ? fmtDay(st.asOf) : fmtDayTime(new Date().toISOString())) + '</span>: ' + bits.map((b) => '<span>' + esc(b) + '</span>').join('; ') + (st.negative ? ' <span>(' + st.negative + ' negative lines, kept as they are)</span>' : '') + '.</div>' +
          (without ? '<div style="color:var(--warn-deep)">Each store\'s price and what is on order were not kept: they need the v241-0 database update first. The stock itself loaded.</div>' : ''));
        say(done.join(''));
        continue;
      }
      if (sheet.kind === 'promo') {
        // v136.20: the active promotions, the whole list for the country (the first chunk clears the old)
        const pr = parsePromo(sheet.aoa, file.name);
        if (!pr.country) throw new Error(file.name + ': could not tell whether this is the UAE or the Qatar file. Put UAE or Qatar in the file name.');
        const label = (pr.country === 'QATAR' ? 'Qatar' : 'UAE') + ' promotions';
        const asOf = pr.asOf || todayDubai();
        const step = (t) => say(done.join('') + '⏳ ' + esc(t));
        await sendCatalogue(pr.country, 'promo', pr.rows.map((r) => Object.assign({ as_of: asOf }, r)), label, step, true);
        await api('/api/scan/upload/catalogue-done', Object.assign({ country: pr.country, file: 'promo', filename: file.name, products: new Set(pr.rows.map((r) => r.prod_code)).size,
          lines: pr.rows.length, promotions: pr.promotions, stores: pr.branches.length, skipped: pr.skipped }, pr.asOf ? { as_of: pr.asOf } : {}));
        done.push('<div>✅ <span>' + esc(label) + '</span>: <span>' + pr.promotions + ' promotions, ' + pr.rows.length.toLocaleString() + ' store lines in ' + pr.branches.length + ' stores</span>' +
          (pr.skipped ? ' <span>(' + pr.skipped + ' repeated lines: the lower promotion price kept)</span>' : '') + '.</div>');
        say(done.join(''));
        continue;
      }
      let parsed;
      if (sheet.kind === 'teal') {
        try { parsed = parseTeal(sheet.aoa, file.name); }
        catch (e) { if (!e.needPeriod) throw e; parsed = parseTeal(sheet.aoa, file.name, askPeriod(file.name)); }
      } else parsed = parseDsr(sheet.aoa, file.name);
      if (!parsed.country) throw new Error(file.name + ': could not tell whether this is the UAE or the Qatar file. Put UAE or Qatar in the file name.');
      const label = parsed.country === 'QATAR' ? 'Qatar' : 'UAE';
      const start = await api('/api/scan/upload/start', {
        country: parsed.country, period_start: parsed.periodStart, period_end: parsed.periodEnd,
        filename: file.name, lines: parsed.lines, skus: parsed.skus || parsed.products.length, branches: parsed.branches,
        totals_only: parsed.totalsOnly === true,
      });
      const jobs = [];
      chunk(parsed.products, CHUNK).forEach((rows) => jobs.push({ kind: 'products', rows }));
      chunk(parsed.stock, CHUNK).forEach((rows) => jobs.push({ kind: 'stock', rows }));
      chunk(parsed.daily, CHUNK).forEach((rows) => jobs.push({ kind: 'daily', rows }));
      for (let i = 0; i < jobs.length; i++) {
        say(done.join('') + '⏳ ' + esc(label) + ': sending ' + (i + 1) + ' of ' + jobs.length + '…');
        await api('/api/scan/upload/rows', { upload_id: start.upload_id, kind: jobs[i].kind, rows: jobs[i].rows });
      }
      done.push('<div>✅ ' + esc(label) + ': ' + esc(fmtDay(parsed.periodStart)) + ' to ' + esc(fmtDay(parsed.periodEnd)) + ', ' +
        (parsed.skus || parsed.products.length) + ' products, ' + parsed.branches.length + ' stores, ' + parsed.lines.toLocaleString() + ' sales lines' + (parsed.totalsOnly ? ' (weekly totals)' : '') + '.</div>');
      say(done.join(''));
    }
  } catch (e) {
    const msg = dbMessage(e.message);
    say(done.join('') + '<div style="color:var(--red)">✗ <span>' + esc(msg) + '</span> <span>Nothing after this point was saved; upload the file again.</span></div>');
  } finally {
    _busy = false;
    const fi = document.getElementById('scanAdminFile');
    if (fi) fi.value = '';
    refreshStatus();
  }
}
